import type { ShapeHandle, WallShape } from '@walls/store';
import { getLineAttributes } from '../../drawing/utils/shapeAttributes';
import type { BoardPoint, ShapeBounds } from './shapeGeometry';

const lineHitTolerance = 4;
const lineHandleTolerance = 5;
const lineEndpointSnapTolerance = 5;

export function getLineBounds(shape: WallShape): ShapeBounds {
  const attributes = getLineAttributes(shape);
  const padding = Math.max(attributes.lineThickness / 2, lineHitTolerance);
  const x = Math.min(attributes.startX, attributes.endX) - padding;
  const y = Math.min(attributes.startY, attributes.endY) - padding;

  return {
    height: Math.abs(attributes.endY - attributes.startY) + padding * 2,
    width: Math.abs(attributes.endX - attributes.startX) + padding * 2,
    x,
    y,
  };
}

export function getLineMenuPosition(shape: WallShape): BoardPoint {
  const attributes = getLineAttributes(shape);
  const topPoint =
    attributes.startY < attributes.endY ||
    (attributes.startY === attributes.endY && attributes.startX < attributes.endX)
      ? { x: attributes.startX, y: attributes.startY }
      : { x: attributes.endX, y: attributes.endY };

  return {
    x: topPoint.x,
    y: topPoint.y - attributes.lineThickness / 2,
  };
}

export function hitTestLine(shape: WallShape, point: BoardPoint, zoom = 1) {
  const distance = getLineDistance(shape, point);

  return distance.x <= lineHitTolerance / zoom && distance.y <= lineHitTolerance / zoom;
}

export function hitTestLineHandle(shape: WallShape, point: BoardPoint): ShapeHandle {
  const attributes = getLineAttributes(shape);

  if (
    point.x >= attributes.startX - lineHandleTolerance &&
    point.x <= attributes.startX + lineHandleTolerance &&
    point.y >= attributes.startY - lineHandleTolerance &&
    point.y <= attributes.startY + lineHandleTolerance
  ) {
    return 'start';
  }

  if (
    point.x >= attributes.endX - lineHandleTolerance &&
    point.x <= attributes.endX + lineHandleTolerance &&
    point.y >= attributes.endY - lineHandleTolerance &&
    point.y <= attributes.endY + lineHandleTolerance
  ) {
    return 'end';
  }

  return null;
}

export function moveLineAttributes(shape: WallShape, deltaX: number, deltaY: number) {
  const attributes = getLineAttributes(shape);

  return {
    startX: attributes.startX + deltaX,
    startY: attributes.startY + deltaY,
    endX: attributes.endX + deltaX,
    endY: attributes.endY + deltaY,
  };
}

export function resizeLineAttributes(
  shape: WallShape,
  handle: 'start' | 'end',
  deltaX: number,
  deltaY: number
) {
  const attributes = getLineAttributes(shape);

  return handle === 'start'
    ? { startX: attributes.startX + deltaX, startY: attributes.startY + deltaY }
    : { endX: attributes.endX + deltaX, endY: attributes.endY + deltaY };
}

export function snapLineEndpoint(shape: WallShape, updatedPoint: 'start' | 'end'): WallShape {
  const attributes = getLineAttributes(shape);
  const snappedAttributes = { ...shape.attributes };

  if (updatedPoint === 'start') {
    if (isWithinSnapTolerance(attributes.startX, attributes.endX)) {
      snappedAttributes.startX = attributes.endX;
    }

    if (isWithinSnapTolerance(attributes.startY, attributes.endY)) {
      snappedAttributes.startY = attributes.endY;
    }
  } else {
    if (isWithinSnapTolerance(attributes.endX, attributes.startX)) {
      snappedAttributes.endX = attributes.startX;
    }

    if (isWithinSnapTolerance(attributes.endY, attributes.startY)) {
      snappedAttributes.endY = attributes.startY;
    }
  }

  return {
    ...shape,
    attributes: snappedAttributes,
  };
}

function getLineDistance(shape: WallShape, point: BoardPoint) {
  const attributes = getLineAttributes(shape);
  const atob = {
    x: attributes.endX - attributes.startX,
    y: attributes.endY - attributes.startY,
  };
  const atop = { x: point.x - attributes.startX, y: point.y - attributes.startY };
  const length = atob.x * atob.x + atob.y * atob.y;
  const t =
    length === 0 ? 0 : Math.min(1, Math.max(0, (atop.x * atob.x + atop.y * atob.y) / length));
  const linePointX = attributes.startX + atob.x * t;
  const linePointY = attributes.startY + atob.y * t;

  return {
    x: Math.abs(linePointX - point.x),
    y: Math.abs(linePointY - point.y),
  };
}

function isWithinSnapTolerance(value: number, target: number) {
  return value >= target - lineEndpointSnapTolerance && value <= target + lineEndpointSnapTolerance;
}
